import { useEffect, useState } from 'react';
import styled from 'styled-components';

import WebSdkPlay from '../WebSdkPlay';
import LeftSideMenu from './PlayCCTV/LeftSideMenu';
import * as cctv from '../../../utils/cctv';
import { WebSDKcctv } from '../../../utils/webSDKcctvClass';
import { useFetchCctvList } from '../../../services/useFetchCctvList';
import { useGetCctvSettings } from '../../../hooks/useCctvSettings';
import IssueGuide from '../../../components/IssueGuide';
import { LoadingModalBackground } from '../../../assets/styles/Modal';
import { PulseLoader } from 'react-spinners';
import { MOBILE_NOT_SUPPORT, streamSelectBoxOptions } from '../../../_constants';
import useWindows from '../../../hooks/useWindows';
import { useTranslation } from 'react-i18next';
import { isMobile } from '../../../utils/checkMobile';
import illustrator from '../../../assets/images/illustration/74.svg';
import { BtnGray } from '../../../components/Button';
import { useNavigate } from 'react-router-dom';

const Root = styled.div`
  width: 100vw;
  width: 100dvw;
  height: 100vh;
  height: 100dvh;
  display: flex;
  .main {
    flex: 1 0;
    display: flex;
    flex-direction: column;
    background-color: var(--black);
  }
  &.isDragging * {
    cursor: grabbing !important;
  }

  .emptyData {
    width: 100%;
    height: calc(100% - 4rem);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    img {
      width: inherit;
      height: inherit;
      max-width: 20rem;
      max-height: 20rem;
      object-fit: contain;
      -webkit-user-drag: none;
    }
    .main_text {
      padding: 1rem 1.5rem;
      background-color: var(--tonal);
      color: var(--inverse-alpha-75);
      border-radius: 0.5rem;
      margin: 0 1rem;
      .font_semibold {
        margin-left: 0.25rem;
      }
      .seperator {
        margin: 0 0.5rem;
        border-left: 1px solid var(--outline-em);
      }
    }

    button {
      margin-top: 4.2rem;
    }
  }
`;

const PlayCCTV = () => {
  const mobile = isMobile();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cameraInfo, nvrList, setNvrList, isError: isErrorCctvList, isLoading: isLoadingCctvList } = useFetchCctvList();
  const [visiblePanel, setVisiblePanel] = useState(true); // 왼쪽 사이드 패널 접힘/펼침 상태

  const [draggingCamInfo, setDraggingCamInfo] = useState<ISubMenu[] | null>(null); // 드래그한 카메라객체 정보
  const [isDragging, setIsDragging] = useState(false); // 마우스 드래그 상태 - 커서 모양 변경 용도
  const [cctvOBJ, setCctvOBJ] = useState<WebSDKcctv>();
  const [droppedWndIndex, setDroppedWndIndex] = useState<null | number>(null); // 드래그한 카메라객체를 드랍한 윈도우 인덱스
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { data: cctvSettings, isLoading: isCctvSettingsLoading, isError: isCctvSettingsError, isFetching: isCctvSettingFetching, isRefetching: isCctvSettingRefetching } = useGetCctvSettings(); // CCTV 설정
  const [selectedCctvCameraInfo, setSelectedCctvCameraInfo] = useState<SelectedCctvCameraInfo>({} as SelectedCctvCameraInfo);
  // const [cctvDivisions, setCctvDivisions] = useState<number>(Number(cctvSettings?.camCType) || 2);
  const [cctvDivisions, setCctvDivisions] = useState<1 | 2 | 3 | 4>(2);
  const [dayRecordFiles, setDayRecordFiles] = useState<Playback[]>([]); // 해당일자의 녹화파일 리스트
  const [selectedStream, setSelectedStream] = useState<Stream>(streamSelectBoxOptions[0]); // 녹화파일 검색 스트림 선택값

  // useClocks 커스텀 hook 선언
  const windows = useWindows();

  // plugin 제거
  useEffect(() => {
    if (cctv.getPluginOBJECT()?.oPlugin) {
      cctv.destroy();
    }
  }, []);

  // 뒤로가기
  const onClickBack = () => {
    navigate(-1);
  };

  const isError = isErrorCctvList || isCctvSettingsError;
  const isLoading = isLoadingCctvList || isCctvSettingsLoading || isCctvSettingFetching || isCctvSettingRefetching;

  if (isError) {
    return <IssueGuide />;
  }

  if (isLoading) {
    return (
      <LoadingModalBackground>
        <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' />
      </LoadingModalBackground>
    );
  }

  if (mobile) {
    return (
      <Root>
        <div className='emptyData'>
          <div>
            <img src={illustrator} alt='' />
            <p className='main_text text-center'>{MOBILE_NOT_SUPPORT}</p>
          </div>
          <BtnGray onClick={onClickBack}>뒤로가기</BtnGray>
        </div>
      </Root>
    );
  }

  return (
    <Root className={isDragging ? 'isDragging' : ''}>
      <LeftSideMenu
        cctvOBJ={cctvOBJ}
        visiblePanel={visiblePanel}
        draggingCamInfo={draggingCamInfo}
        setDraggingCamInfo={setDraggingCamInfo}
        nvrList={nvrList}
        setNvrList={setNvrList}
        isLoading={isLoading}
        isError={isError}
        setIsDragging={setIsDragging}
        selectedCctvCameraInfo={selectedCctvCameraInfo}
        cctvDivisions={cctvDivisions}
        cctvSettings={cctvSettings}
        setDayRecordFiles={setDayRecordFiles}
        selectedStream={selectedStream}
        setSelectedStream={setSelectedStream}
        setDroppedWndIndex={setDroppedWndIndex} // 드래그한 카메라 객체를 드랍한 윈도우 인덱스 변경 함수
      />
      <div className='main'>
        <WebSdkPlay
          cctvOBJ={cctvOBJ} // cctv 객체
          setCctvOBJ={setCctvOBJ} // cctv 객체 변경 함수
          draggingCamInfo={draggingCamInfo} // 드래그한 카메라 객체 정보
          setDraggingCamInfo={setDraggingCamInfo} // 드래그한 카메라 객체 정보 변경 함수
          droppedWndIndex={droppedWndIndex} // 드래그한 카메라 객체를 드랍한 윈도우 인덱스
          setDroppedWndIndex={setDroppedWndIndex} // 드래그한 카메라 객체를 드랍한 윈도우 인덱스 변경 함수
          isDragging={isDragging} // 드래그 상태
          setIsDragging={setIsDragging} // 드래그 상태 변경 함수
          cctvSettings={cctvSettings || null}
          cameraInfo={cameraInfo}
          visiblePanel={visiblePanel} // 왼쪽 사이드 패널 접기/펼치기 상태
          setVisiblePanel={setVisiblePanel} // 왼쪽 사이드 패널 상태 변경 함수
          nvrList={nvrList}
          setNvrList={setNvrList}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          selectedCameraInfo={selectedCctvCameraInfo}
          setSelectedCameraInfo={setSelectedCctvCameraInfo}
          dayRecordFiles={dayRecordFiles} // 해당일자의 녹화파일 리스트
          selectedStream={selectedStream}
          cctvDivisions={cctvDivisions}
          setCctvDivisions={setCctvDivisions}
          windows={windows}
        />
      </div>
    </Root>
  );
};

export default PlayCCTV;
